import * as React from "react";
import { Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { AppPopoverComponent } from "./app-popover-component";
import { MultiCheckboxDropdown } from "./multi-checkbox-dropdown";
import { nodes as nodeConfigs } from "@/config/nodes";
import { AppContextType } from "@/context/app";

export interface NodeConfigApp {
  id: string;
  name: string;
  template: string;
  type: string;
  config: Record<string, unknown>;
}

interface NodeConfigTableProps {
  apps: NodeConfigApp[];
  errors?: unknown;
  onUpdateApp: (id: string, updates: Partial<NodeConfigApp>) => void;
  onRemoveApp: (id: string) => void;
  hideRemoveIcon?: boolean;
}

export const NodeConfigTable = ({
  apps,
  errors,
  onUpdateApp,
  onRemoveApp,
  hideRemoveIcon = false,
}: NodeConfigTableProps) => {
  return (
    <div className="mt-8 overflow-hidden rounded-lg border border-border bg-card/50">
      <Table>
        <TableHeader className="bg-muted/50">
          <TableRow className="hover:bg-transparent">
            {!hideRemoveIcon && <TableHead className="w-[40px]"></TableHead>}
            <TableHead className="w-[150px] text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Type</TableHead>
            <TableHead className="w-[180px] text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Node Name</TableHead>
            <TableHead className="w-[280px] text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Starter Template</TableHead>
            <TableHead className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Configuration Options</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {apps.map((app, idx) => {
            const nodeConfig = nodeConfigs[app.type as keyof typeof nodeConfigs];
            if (!nodeConfig) return null;

            return (
              <TableRow key={app.id} className="border-border hover:bg-muted/30">
                {!hideRemoveIcon && (
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => onRemoveApp(app.id)}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </TableCell>
                )}
                <TableCell>
                  <Badge
                    variant="outline"
                    className="inline-flex w-[140px] items-center justify-start gap-2 bg-secondary/50 px-2 py-1 text-foreground border-border"
                  >
                    <span className="text-primary scale-90">{nodeConfig.icon}</span>
                    <span className="text-xs font-bold text-muted-foreground lowercase">{app.type}</span>
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <Input
                      value={app.name}
                      onChange={(e) => onUpdateApp(app.id, { name: e.target.value })}
                      className={cn(
                        "h-9 border-input bg-background shadow-none focus-visible:ring-1 text-xs font-medium",
                        (errors as Record<number, { name?: { message?: string } } | undefined>)?.[idx]?.name && "border-destructive focus-visible:ring-destructive"
                      )}
                    />
                    {((errors as Record<number, { name?: { message?: string } } | undefined>)?.[idx]?.name) && (
                      <span className="text-[10px] text-destructive font-medium">
                        {(errors as Record<number, { name?: { message?: string } } | undefined>)[idx]!.name!.message}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <AppPopoverComponent
                    idx={idx}
                    app={app as unknown as AppContextType}
                    handleAppEdit={(_, editedApp) => onUpdateApp(app.id, { 
                      template: editedApp.template,
                      config: editedApp.config 
                    })}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex flex-row flex-wrap items-center gap-x-6 gap-y-2">
                    {nodeConfig.checkboxOptions && (
                      <div className="flex flex-wrap gap-4">
                        {Object.entries(nodeConfig.checkboxOptions).map(([key, option]: [string, { label: string }]) => (
                          <div key={key} className="flex items-center gap-2.5">
                            <Checkbox
                              id={`${app.id}-${key}`}
                              checked={!!app.config?.[key]}
                              onCheckedChange={(checked) => 
                                onUpdateApp(app.id, { 
                                  config: { ...app.config, [key]: !!checked } 
                                })
                              }
                              className="h-4 w-4 border-input data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                            />
                            <label
                              htmlFor={`${app.id}-${key}`}
                              className="text-[11px] font-bold tracking-tight text-foreground cursor-pointer select-none whitespace-nowrap"
                            >
                              {option.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                    {nodeConfig.comboboxOptions && (
                      <div className="flex flex-wrap gap-4">
                        {Object.entries(nodeConfig.comboboxOptions).map(([key, config]: [string, { label: string; options: { value: string; label: string; icon: React.ReactNode; disabled?: boolean }[] }]) => (
                          <div key={key} className="flex items-center gap-3">
                            <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                              {config.label}
                            </label>
                            <div className="min-w-[200px]">
                              <MultiCheckboxDropdown
                                options={config.options}
                                value={(app.config?.[key === 'provider' ? 'providers' : key] as string[]) || []}
                                onChange={(values) => {
                                  const activeValues = Object.keys(values).filter(v => values[v]);
                                  onUpdateApp(app.id, {
                                    config: { 
                                      ...app.config, 
                                      [key === 'provider' ? 'providers' : key]: activeValues 
                                    }
                                  });
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {nodeConfig.inputOptions && (
                      <div className="flex flex-wrap gap-4">
                        {Object.entries(nodeConfig.inputOptions).map(([key, config]: [string, { label: string }]) => (
                          <div key={key} className="flex items-center gap-3">
                            <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                              {config.label}
                            </label>
                            <Input
                              type={key === 'port' ? 'number': 'text'}
                              value={(app.config?.[key] as string) || ""}
                              onChange={(e) => 
                                onUpdateApp(app.id, { 
                                  config: { ...app.config, [key]: e.target.value } 
                                })
                              }
                              className="h-8 w-[80px] border-input bg-background shadow-none focus-visible:ring-1 text-[11px] font-medium"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
